import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { getRpRevenueData, RevenueDataPoint } from '../api';
import { 
  addMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfDay, 
  endOfDay, 
  format, 
  parseISO, 
  subYears,
  startOfYear,
  endOfYear
} from 'date-fns';

type PeriodValue = 'day' | 'week' | 'month' | 'year';

interface Period {
  label: string;
  value: PeriodValue;
}

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    fill: boolean;
    borderColor: string;
    tension: number;
    borderWidth: number;
    pointRadius: number;
  }[];
}

const periods: Period[] = [
  { label: 'Daily', value: 'day' },
  { label: 'Weekly', value: 'week' },
  { label: 'Monthly', value: 'month' },
  { label: 'Yearly', value: 'year' }
];

const RevenueGraph: React.FC = () => {
  const [startDate, setStartDate] = useState<Date>(startOfYear(new Date())); // Default to start of current year
  const [endDate, setEndDate] = useState<Date>(endOfYear(new Date())); // Default to end of current year
  const [selectedPeriod, setSelectedPeriod] = useState<Period>(periods[2]); // Default to monthly
  const [totalRevenue, setTotalRevenue] = useState<number>(0);
  const [chartData, setChartData] = useState<ChartData>({
    labels: [],
    datasets: [
      {
        label: 'Revenue',
        data: [],
        fill: false,
        borderColor: '#4CAF50',
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 4
      }
    ]
  });

  const updateDateRange = (period: Period) => {
    const now = new Date();
    switch (period.value) {
      case 'day':
      case 'week':
        // Show current month for both daily and weekly views
        setStartDate(startOfMonth(now));
        setEndDate(endOfMonth(now));
        break;
      case 'month':
        // Show current year for monthly view
        setStartDate(startOfYear(now));
        setEndDate(endOfYear(now));
        break;
      case 'year':
        // Show last 3 years for yearly view
        setStartDate(startOfYear(subYears(now, 2)));
        setEndDate(endOfYear(now));
        break;
    }
  };

  const fetchData = async () => {
    try {
      // Ensure we're using the full day range for the dates
      const adjustedStartDate = startOfDay(startDate);
      const adjustedEndDate = endOfDay(endDate);

      console.log('Fetching revenue data with:', {
        startDate: adjustedStartDate.toISOString(),
        endDate: adjustedEndDate.toISOString(),
        period: selectedPeriod.value
      });

      const data = await getRpRevenueData(adjustedStartDate, adjustedEndDate, selectedPeriod.value);
      console.log('Received revenue data:', data);
      
      if (!Array.isArray(data) || data.length === 0) {
        console.log('No data received or empty array');
        setTotalRevenue(0);
        setChartData({
          labels: [],
          datasets: [
            {
              label: 'Revenue',
              data: [],
              fill: false,
              borderColor: '#4CAF50',
              tension: 0.4,
              borderWidth: 3,
              pointRadius: 4
            }
          ]
        });
        return;
      }
      
      // Format dates based on the selected period
      const formattedData = data.map((item: RevenueDataPoint) => ({
        ...item,
        date: formatDate(parseISO(item.date), selectedPeriod.value)
      }));
      console.log('Formatted data:', formattedData);

      // Calculate total revenue
      const total = data.reduce((sum, item) => sum + (Number(item.revenue) || 0), 0);
      console.log('Calculated total revenue:', total);
      setTotalRevenue(total);

      const newChartData = {
        labels: formattedData.map((item: RevenueDataPoint) => item.date),
        datasets: [
          {
            label: 'Revenue',
            data: formattedData.map((item: RevenueDataPoint) => Number(item.revenue) || 0),
            fill: false,
            borderColor: '#4CAF50',
            tension: 0.4,
            borderWidth: 3,
            pointRadius: 4
          }
        ]
      };
      console.log('Setting new chart data:', newChartData);

      setChartData(newChartData);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      // Reset the chart on error
      setTotalRevenue(0);
      setChartData({
        labels: [],
        datasets: [
          {
            label: 'Revenue',
            data: [],
            fill: false,
            borderColor: '#4CAF50',
            tension: 0.4,
            borderWidth: 3,
            pointRadius: 4
          }
        ]
      });
    }
  };

  useEffect(() => {
    console.log('Effect triggered with period:', selectedPeriod.value);
    fetchData();
  }, [startDate, endDate, selectedPeriod]);

  // Set initial date range when component mounts
  useEffect(() => {
    updateDateRange(selectedPeriod);
  }, []);

  const formatDate = (date: Date, periodType: PeriodValue): string => {
    switch (periodType) {
      case 'day':
        return format(date, 'MMM d, yyyy');
      case 'week':
        return format(date, "'Week of' MMM d, yyyy");
      case 'month':
        return format(date, 'MMMM yyyy');
      case 'year':
        return format(date, 'yyyy');
      default:
        return format(date, 'MMM d, yyyy');
    }
  };

  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#495057',
          font: {
            size: 14
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            return `Revenue: ${formatCurrency(context.raw)}`;
          }
        },
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 14
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#495057',
          font: {
            size: 12
          },
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          color: '#ebedef',
          drawBorder: false
        }
      },
      y: {
        ticks: {
          color: '#495057',
          callback: (value: number) => formatCurrency(value),
          font: {
            size: 12
          }
        },
        grid: {
          color: '#ebedef',
          drawBorder: false
        },
        beginAtZero: true
      }
    }
  };

  const header = (
    <div className="flex flex-column p-4">
      <div className="text-2xl font-bold mb-3">Revenue Over Time</div>
      <div className="text-xl">
        Total Revenue: <span className="font-bold text-green-600">{formatCurrency(totalRevenue)}</span>
      </div>
    </div>
  );

  const handlePeriodChange = (e: any) => {
    console.log('Period change event:', e);
    const newPeriod = periods.find(p => p.value === e.value);
    if (newPeriod) {
      console.log('Setting new period:', newPeriod);
      setSelectedPeriod(newPeriod);
      updateDateRange(newPeriod);
    }
  };

  return (
    <Card header={header} className="mb-6 shadow-2">
      <div className="grid mb-4">
        <div className="col-12 md:col-4 px-2">
          <label htmlFor="startDate" className="block text-lg font-medium mb-2">Start Date</label>
          <Calendar
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.value as Date)}
            showIcon
            dateFormat="mm/dd/yy"
            maxDate={endDate}
            className="w-full"
          />
        </div>
        <div className="col-12 md:col-4 px-2">
          <label htmlFor="endDate" className="block text-lg font-medium mb-2">End Date</label>
          <Calendar
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.value as Date)}
            showIcon
            dateFormat="mm/dd/yy"
            minDate={startDate}
            className="w-full"
          />
        </div>
        <div className="col-12 md:col-4 px-2">
          <label htmlFor="period" className="block text-lg font-medium mb-2">Period</label>
          <Dropdown
            id="period"
            value={selectedPeriod.value}
            options={periods}
            onChange={handlePeriodChange}
            optionLabel="label"
            optionValue="value"
            className="w-full"
          />
        </div>
      </div>
      <div style={{ width: '100%', height: '600px' }}>
        <Chart type="line" data={chartData} options={chartOptions} />
      </div>
    </Card>
  );
};

export default RevenueGraph;
